import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { Button } from '../UI';
import {
  User, Heart, Package, ShoppingCart, BookOpen,
  Settings, Users, ChevronRight, Plus, Archive,
  Shield, Clock, Lock, Globe
} from 'lucide-react';
import { useFavorites } from '../../hooks/useFavorites';
import { usePantries } from '../../hooks/usePantries';
import { useGroceryLists } from '../../hooks/useGroceryLists';
import { useCookbooks } from '../../hooks/useCookbooks';
import CareProfilesHub from '../CareProfiles/CareProfilesHub';

const DashboardView = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const { favoriteRecipes = [] } = useFavorites();
  const { pantries = [] } = usePantries();
  const { groceryLists = [], archivedLists = [] } = useGroceryLists();
  const { cookbooks = [] } = useCookbooks();

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto text-center py-xxxl">
        <div className="p-xl rounded-large bg-surface border border-text-tertiary/10">
          <User size={48} className="text-primary mx-auto mb-lg" />
          <h2 className="text-title-2 text-primary mb-md">Sign In Required</h2>
          <p className="text-body text-text-secondary mb-lg">
            Sign in to access your personalized dashboard
          </p>
          <div className="flex gap-md justify-center">
            <Button variant="outline" onClick={() => navigate('/login')}>Sign In</Button>
            <Button variant="primary" onClick={() => navigate('/signup')}>Create Account</Button>
          </div>
        </div>
      </div>
    );
  }

  // Derived data
  const activeList = groceryLists[0] || null;
  const activeListItems = activeList?.items || [];
  const checkedCount = activeListItems.filter(i => i.isChecked).length;
  const totalCount = activeListItems.length;

  const tabs = [
    { id: 'overview',      label: 'Overview',      icon: null  },
    { id: 'care-profiles', label: 'Care Profiles', icon: Users },
    { id: 'lists',         label: 'Lists',         icon: ShoppingCart },
    { id: 'settings',      label: 'Settings',      icon: Settings },
  ];

  return (
    <div className="space-y-lg">

      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-display font-rounded text-primary mb-xs">
            {user.displayName ? `Welcome, ${user.displayName.split(' ')[0]}` : 'Dashboard'}
          </h1>
          <p className="text-body text-text-secondary">Your personal nutrition hub</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-sm border-b border-text-tertiary/10">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-sm px-lg py-md text-callout font-semibold transition-all border-b-2 ${
                activeTab === tab.id
                  ? 'text-primary border-primary'
                  : 'text-text-secondary border-transparent hover:text-primary'
              }`}
            >
              {Icon && <Icon size={16} />}
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-lg">

          {/* LEFT — main content */}
          <div className="lg:col-span-2 space-y-lg">

            {/* Active Grocery List */}
            <section>
              <div className="flex items-center justify-between mb-md">
                <h2 className="text-title-2 font-rounded text-primary">Active List</h2>
                <button
                  onClick={() => navigate('/products')}
                  className="text-caption text-primary font-semibold hover:underline flex items-center gap-xs"
                >
                  All Lists <ChevronRight size={14} />
                </button>
              </div>

              {activeList ? (
                <div
                  className="p-lg rounded-large bg-surface cursor-pointer hover:border-primary/30 transition-all"
                  style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
                  onClick={() => navigate('/products')}
                >
                  <div className="flex items-center justify-between mb-md">
                    <div className="flex items-center gap-md">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <ShoppingCart size={18} className="text-primary" />
                      </div>
                      <div>
                        <h3 className="text-callout font-semibold text-text-primary">{activeList.name}</h3>
                        <p className="text-caption text-text-secondary">
                          {totalCount} {totalCount === 1 ? 'item' : 'items'}
                          {totalCount > 0 && ` · ${checkedCount} checked`}
                        </p>
                      </div>
                    </div>
                    <ChevronRight size={18} className="text-text-tertiary" />
                  </div>

                  {totalCount > 0 ? (
                    <>
                      <div className="w-full bg-text-tertiary/10 rounded-full h-1.5 mb-md">
                        <div
                          className="bg-primary h-1.5 rounded-full transition-all"
                          style={{ width: `${totalCount > 0 ? (checkedCount / totalCount) * 100 : 0}%` }}
                        />
                      </div>
                      <div className="flex flex-col gap-xs">
                        {activeListItems.slice(0, 4).map(item => (
                          <div key={item.id} className="flex items-center gap-sm">
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                              item.isChecked ? 'bg-primary border-primary' : 'border-text-tertiary/30'
                            }`}>
                              {item.isChecked && (
                                <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                                  <path d="M1.5 4L3 5.5L6.5 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                            </div>
                            <span className={`text-caption ${item.isChecked ? 'line-through text-text-tertiary' : 'text-text-primary'}`}>
                              {item.name}
                            </span>
                            {item.quantity > 1 && (
                              <span className="text-caption text-text-tertiary ml-auto">x{item.quantity}</span>
                            )}
                          </div>
                        ))}
                        {totalCount > 4 && (
                          <p className="text-caption text-text-tertiary mt-xs">+{totalCount - 4} more items</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="text-caption text-text-tertiary">List is empty — add items from Products</p>
                  )}
                </div>
              ) : (
                <div
                  className="p-lg rounded-large bg-surface text-center"
                  style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
                >
                  <ShoppingCart size={32} className="text-text-tertiary mx-auto mb-sm" />
                  <p className="text-callout text-text-secondary mb-md">No active grocery list</p>
                  <Button variant="outline" size="small" onClick={() => navigate('/products')}>
                    <Plus size={14} className="mr-xs" />
                    Create List
                  </Button>
                </div>
              )}
            </section>

            {/* Cookbooks */}
            <section>
              <div className="flex items-center justify-between mb-md">
                <h2 className="text-title-2 font-rounded text-primary">Cookbooks</h2>
                <button
                  onClick={() => navigate('/recipes')}
                  className="text-caption text-primary font-semibold hover:underline flex items-center gap-xs"
                >
                  View All <ChevronRight size={14} />
                </button>
              </div>

              {cookbooks.length === 0 ? (
                <div
                  className="p-lg rounded-large bg-surface text-center"
                  style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
                >
                  <BookOpen size={32} className="text-text-tertiary mx-auto mb-sm" />
                  <p className="text-callout text-text-secondary mb-md">No cookbooks yet</p>
                  <Button variant="outline" size="small" onClick={() => navigate('/recipes')}>
                    <Plus size={14} className="mr-xs" />
                    Create Cookbook
                  </Button>
                </div>
              ) : (
                <div className="flex gap-md overflow-x-auto pb-sm" style={{ scrollbarWidth: 'none' }}>
                  {/* Create new card */}
                  <button
                    onClick={() => navigate('/recipes')}
                    className="flex-shrink-0 w-[160px] h-[140px] rounded-large flex flex-col items-center justify-center gap-sm hover:bg-primary/5 transition-all"
                    style={{ border: '2px dashed rgba(var(--color-text-tertiary),0.2)' }}
                  >
                    <Plus size={20} className="text-text-tertiary" />
                    <span className="text-caption text-text-tertiary font-semibold">New Cookbook</span>
                  </button>

                  {cookbooks.map(cb => (
                    <div
                      key={cb.id}
                      onClick={() => navigate('/recipes')}
                      className="flex-shrink-0 w-[160px] h-[140px] rounded-large overflow-hidden relative cursor-pointer hover:shadow-medium transition-all"
                      style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
                    >
                      {cb.coverImageURL ? (
                        <img src={cb.coverImageURL} alt={cb.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary/10 to-accent/5 flex items-center justify-center">
                          <BookOpen size={28} className="text-primary/40" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex flex-col justify-end p-sm">
                        <p className="text-caption font-semibold text-white line-clamp-1">{cb.name}</p>
                        <div className="flex items-center gap-xs mt-xs">
                          {cb.isPublic
                            ? <Globe size={10} className="text-white/70" />
                            : <Lock size={10} className="text-white/70" />
                          }
                          <p className="text-xs text-white/70">{(cb.recipeIds || []).length} recipes</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Pantries */}
            <section>
              <div className="flex items-center justify-between mb-md">
                <h2 className="text-title-2 font-rounded text-primary">Pantries</h2>
                <button
                  onClick={() => navigate('/products')}
                  className="text-caption text-primary font-semibold hover:underline flex items-center gap-xs"
                >
                  View All <ChevronRight size={14} />
                </button>
              </div>

              {pantries.length === 0 ? (
                <div
                  className="p-lg rounded-large bg-surface text-center"
                  style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
                >
                  <Package size={32} className="text-text-tertiary mx-auto mb-sm" />
                  <p className="text-callout text-text-secondary mb-md">No pantries yet</p>
                  <Button variant="outline" size="small" onClick={() => navigate('/products')}>
                    <Plus size={14} className="mr-xs" />
                    Create Pantry
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-md">
                  {pantries.map(pantry => (
                    <div
                      key={pantry.id}
                      onClick={() => navigate('/products')}
                      className="p-md rounded-large bg-surface cursor-pointer hover:border-primary/30 transition-all"
                      style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
                    >
                      <div className="w-9 h-9 rounded-full bg-accent/10 flex items-center justify-center mb-sm">
                        <Package size={16} className="text-accent" />
                      </div>
                      <p className="text-callout font-semibold text-text-primary line-clamp-1">{pantry.name}</p>
                      <p className="text-caption text-text-tertiary mt-xs">
                        {(pantry.items || []).length} {(pantry.items || []).length === 1 ? 'item' : 'items'}
                      </p>
                    </div>
                  ))}
                  <button
                    onClick={() => navigate('/products')}
                    className="p-md rounded-large flex flex-col items-center justify-center gap-sm hover:bg-primary/5 transition-all"
                    style={{ border: '2px dashed rgba(var(--color-text-tertiary),0.15)' }}
                  >
                    <Plus size={18} className="text-text-tertiary" />
                    <span className="text-caption text-text-tertiary font-semibold">New Pantry</span>
                  </button>
                </div>
              )}
            </section>

          </div>

          {/* RIGHT — sidebar */}
          <div className="space-y-lg">

            {/* User card */}
            <div
              className="p-lg rounded-large bg-surface"
              style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
            >
              <div className="flex items-center gap-md mb-lg">
                <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <User size={22} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-callout font-semibold text-text-primary truncate">
                    {user.displayName || 'User'}
                  </h3>
                  <p className="text-caption text-text-secondary truncate">{user.email}</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="small"
                onClick={() => setActiveTab('settings')}
                className="w-full"
              >
                Account Settings
              </Button>
            </div>

            {/* Stats */}
            <div
              className="p-lg rounded-large bg-surface"
              style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
            >
              <h3 className="text-callout font-semibold text-text-primary mb-md">Overview</h3>
              <div className="space-y-sm">
                {[
                  { icon: Heart,        label: 'Saved Recipes',  value: favoriteRecipes.length, color: 'text-error'   },
                  { icon: BookOpen,     label: 'Cookbooks',      value: cookbooks.length,        color: 'text-primary' },
                  { icon: ShoppingCart, label: 'Active Lists',   value: groceryLists.length,     color: 'text-primary' },
                  { icon: Archive,      label: 'Archived Lists', value: archivedLists.length,    color: 'text-text-tertiary' },
                  { icon: Package,      label: 'Pantries',       value: pantries.length,         color: 'text-accent'  },
                ].map(({ icon: Icon, label, value, color }) => (
                  <div key={label} className="flex items-center justify-between py-xs">
                    <div className="flex items-center gap-sm">
                      <Icon size={15} className={color} />
                      <span className="text-caption text-text-secondary">{label}</span>
                    </div>
                    <span className="text-callout font-bold text-text-primary">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Care Profiles quick access */}
            <div
              className="p-lg rounded-large bg-surface"
              style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
            >
              <div className="flex items-center justify-between mb-md">
                <div className="flex items-center gap-sm">
                  <Shield size={16} className="text-primary" />
                  <h3 className="text-callout font-semibold text-text-primary">Care Profiles</h3>
                </div>
                <button
                  onClick={() => setActiveTab('care-profiles')}
                  className="text-caption text-primary font-semibold hover:underline"
                >
                  Manage
                </button>
              </div>
              <p className="text-caption text-text-secondary mb-md">
                Filter products and recipes by your household's dietary needs
              </p>
              <Button
                variant="outline"
                size="small"
                onClick={() => setActiveTab('care-profiles')}
                className="w-full"
              >
                <Users size={14} className="mr-xs" />
                View Profiles
              </Button>
            </div>

            {/* Recent grocery lists */}
            {groceryLists.length > 1 && (
              <div
                className="p-lg rounded-large bg-surface"
                style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
              >
                <div className="flex items-center justify-between mb-md">
                  <h3 className="text-callout font-semibold text-text-primary">Other Lists</h3>
                  <button
                    onClick={() => setActiveTab('lists')}
                    className="text-caption text-primary font-semibold hover:underline"
                  >
                    See All
                  </button>
                </div>
                <div className="space-y-sm">
                  {groceryLists.slice(1, 4).map(list => (
                    <div
                      key={list.id}
                      onClick={() => navigate('/products')}
                      className="flex items-center gap-sm cursor-pointer hover:text-primary transition-colors"
                    >
                      <ShoppingCart size={14} className="text-text-tertiary flex-shrink-0" />
                      <span className="text-caption text-text-secondary flex-1 truncate">{list.name}</span>
                      <span className="text-caption text-text-tertiary">{(list.items || []).length}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      )}

      {/* CARE PROFILES TAB */}
      {activeTab === 'care-profiles' && <CareProfilesHub />}

      {/* LISTS TAB */}
      {activeTab === 'lists' && (
        <div className="space-y-lg">
          <div className="flex items-center justify-between">
            <h2 className="text-title-2 font-rounded text-primary">Grocery Lists</h2>
            <Button variant="primary" size="small" onClick={() => navigate('/products')}>
              <Plus size={14} className="mr-xs" />
              New List
            </Button>
          </div>

          {/* Active lists */}
          {groceryLists.length > 0 && (
            <div className="space-y-sm">
              <p className="text-caption text-text-tertiary font-semibold uppercase tracking-wide">Active</p>
              {groceryLists.map(list => {
                const items = list.items || [];
                const checked = items.filter(i => i.isChecked).length;
                return (
                  <div
                    key={list.id}
                    onClick={() => navigate('/products')}
                    className="flex items-center gap-md p-md rounded-large bg-surface cursor-pointer hover:border-primary/30 transition-all"
                    style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
                  >
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <ShoppingCart size={16} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-callout font-semibold text-text-primary">{list.name}</p>
                      <p className="text-caption text-text-secondary">
                        {items.length} items{items.length > 0 ? ` · ${checked} checked` : ''}
                      </p>
                    </div>
                    {list.updatedAt && (
                      <div className="flex items-center gap-xs text-caption text-text-tertiary flex-shrink-0">
                        <Clock size={12} />
                        <span>{list.updatedAt.toLocaleDateString()}</span>
                      </div>
                    )}
                    <ChevronRight size={16} className="text-text-tertiary flex-shrink-0" />
                  </div>
                );
              })}
            </div>
          )}

          {/* Archived lists */}
          {archivedLists.length > 0 && (
            <div className="space-y-sm">
              <p className="text-caption text-text-tertiary font-semibold uppercase tracking-wide">Archived</p>
              {archivedLists.map(list => {
                const items = list.items || [];
                return (
                  <div
                    key={list.id}
                    className="flex items-center gap-md p-md rounded-large bg-surface opacity-70"
                    style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
                  >
                    <div className="w-9 h-9 rounded-full bg-text-tertiary/10 flex items-center justify-center flex-shrink-0">
                      <Archive size={16} className="text-text-tertiary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-callout font-semibold text-text-secondary">{list.name}</p>
                      <p className="text-caption text-text-tertiary">{items.length} items</p>
                    </div>
                    {list.updatedAt && (
                      <span className="text-caption text-text-tertiary flex-shrink-0">
                        {list.updatedAt.toLocaleDateString()}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {groceryLists.length === 0 && archivedLists.length === 0 && (
            <div
              className="p-xl rounded-large bg-surface text-center"
              style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
            >
              <ShoppingCart size={40} className="text-text-tertiary mx-auto mb-md" />
              <p className="text-callout text-text-secondary mb-md">No grocery lists yet</p>
              <Button variant="primary" onClick={() => navigate('/products')}>
                Create Your First List
              </Button>
            </div>
          )}
        </div>
      )}

      {/* SETTINGS TAB */}
      {activeTab === 'settings' && (
        <div className="max-w-2xl space-y-lg">
          <div
            className="p-lg rounded-large bg-surface"
            style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
          >
            <h2 className="text-title-2 font-rounded text-primary mb-lg">Account Settings</h2>
            <div className="space-y-md">
              <div>
                <label className="text-caption text-text-secondary mb-xs block">Display Name</label>
                <input
                  type="text"
                  value={user.displayName || ''}
                  disabled
                  className="w-full px-lg py-md bg-background border border-text-tertiary/20 rounded-medium text-body text-text-primary"
                />
              </div>
              <div>
                <label className="text-caption text-text-secondary mb-xs block">Email</label>
                <input
                  type="email"
                  value={user.email || ''}
                  disabled
                  className="w-full px-lg py-md bg-background border border-text-tertiary/20 rounded-medium text-body text-text-primary"
                />
              </div>
            </div>
          </div>

          <div
            className="p-lg rounded-large bg-surface"
            style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
          >
            <h3 className="text-callout font-semibold text-text-primary mb-md">Manage</h3>
            <div className="space-y-sm">
              <button
                onClick={() => setActiveTab('care-profiles')}
                className="w-full flex items-center justify-between p-md rounded-large hover:bg-primary/5 transition-all text-left"
                style={{ border: '1px solid rgba(var(--color-text-tertiary),0.08)' }}
              >
                <div className="flex items-center gap-md">
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                    <Users size={16} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-callout font-semibold text-text-primary">Care Profiles</p>
                    <p className="text-caption text-text-secondary">Manage dietary profiles for your household</p>
                  </div>
                </div>
                <ChevronRight size={16} className="text-text-tertiary" />
              </button>

              <button
                onClick={() => setActiveTab('lists')}
                className="w-full flex items-center justify-between p-md rounded-large hover:bg-primary/5 transition-all text-left"
                style={{ border: '1px solid rgba(var(--color-text-tertiary),0.08)' }}
              >
                <div className="flex items-center gap-md">
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                    <ShoppingCart size={16} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-callout font-semibold text-text-primary">Grocery Lists</p>
                    <p className="text-caption text-text-secondary">View active and archived lists</p>
                  </div>
                </div>
                <ChevronRight size={16} className="text-text-tertiary" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardView;